import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { SHIFT_CATEGORIES, SURGERY_CATEGORIES, CATEGORY_LABELS, formatDate } from "../common/residentCategories";

const COUNT_COLUMNS = [
  ...SHIFT_CATEGORIES.map(({ key }) => key),
  ...SURGERY_CATEGORIES.map(({ key }) => key),
  "surgeryTotal",
];

// Dates behind one cell, as [{ date, detail }]. The surgery total names the surgery type and
// the totals row (several residents) names the resident.
const cellEntries = (rows, key) => {
  const categories = key === "surgeryTotal" ? SURGERY_CATEGORIES.map((c) => c.key) : [key];
  const entries = [];
  rows.forEach((row) => {
    categories.forEach((category) => {
      // tolerate a backend that predates dates / newer categories
      (row.dates?.[category] || []).forEach((date) => {
        const detail = [rows.length > 1 && row.name, key === "surgeryTotal" && CATEGORY_LABELS[category]]
          .filter(Boolean)
          .join(" · ");
        entries.push({ date, detail });
      });
    });
  });
  entries.sort((a, b) => a.date.localeCompare(b.date) || a.detail.localeCompare(b.detail, "el"));

  // collapse repeats of the same date (and detail) into one line with a count
  const grouped = [];
  entries.forEach((entry) => {
    const last = grouped[grouped.length - 1];
    if (last && last.date === entry.date && last.detail === entry.detail) {
      last.count += 1;
    } else {
      grouped.push({ ...entry, count: 1 });
    }
  });
  return grouped;
};

const DatesTooltip = ({ title, entries, children }) => {
  if (entries.length === 0) {
    return children;
  }
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ maxHeight: 320, overflowY: "auto", pr: "0.25rem" }}>
          <Typography fontSize="0.8rem" fontWeight={700} mb="0.25rem">
            {title}
          </Typography>
          {entries.map(({ date, detail, count }) => (
            <Typography key={date + detail} fontSize="0.8rem" whiteSpace="nowrap">
              {formatDate(date)}
              {detail && ` · ${detail}`}
              {count > 1 && ` ×${count}`}
            </Typography>
          ))}
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
};

const COLUMN_LABELS = { ...CATEGORY_LABELS, surgeryTotal: "Χειρουργεία" };

// The residents spreadsheet: one row per resident, counts per rotation / surgery type, totals row.
const ResidentsTable = ({ data, isLoading, isError }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const rows = data || [];

  const totals = Object.fromEntries(
    COUNT_COLUMNS.map((key) => [key, rows.reduce((sum, row) => sum + (row[key] || 0), 0)])
  );

  const headerBg = isDark ? theme.palette.primary[600] : "#243c6b";
  const border = `1px solid ${theme.palette.divider}`;
  const headCell = {
    backgroundColor: headerBg,
    color: "#ffffff",
    fontWeight: 600,
    textAlign: "center",
    border: `1px solid ${alpha("#ffffff", 0.15)}`,
    whiteSpace: "nowrap",
    py: "0.6rem",
  };
  const subHeadCell = { ...headCell, backgroundColor: alpha(headerBg, 0.85) };
  const bodyCell = { border, textAlign: "center", py: "0.45rem" };
  const totalCell = {
    ...bodyCell,
    fontWeight: 700,
    backgroundColor: alpha(theme.palette.secondary.main, isDark ? 0.16 : 0.12),
  };
  const surgeryTotalCell = { fontWeight: 700 };

  return (
    <Paper sx={{ mt: "1.5rem", backgroundColor: theme.palette.background.alt, overflow: "hidden" }}>
      {isLoading ? (
        <Box display="flex" justifyContent="center" p="3rem">
          <CircularProgress color="secondary" />
        </Box>
      ) : isError ? (
        <Typography color="error" p="1.5rem">
          Failed to load residents.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={{ ...headCell, textAlign: "left" }}>Ειδικευόμενος</TableCell>
                <TableCell rowSpan={2} sx={{ ...headCell, textAlign: "left" }}>Τελική ειδικότητα</TableCell>
                {SHIFT_CATEGORIES.map(({ key, label }) => (
                  <TableCell key={key} rowSpan={2} sx={headCell}>{label}</TableCell>
                ))}
                <TableCell colSpan={SURGERY_CATEGORIES.length + 1} sx={headCell}>Χειρουργεία</TableCell>
              </TableRow>
              <TableRow>
                {SURGERY_CATEGORIES.map(({ key, label }) => (
                  <TableCell key={key} sx={subHeadCell}>{label}</TableCell>
                ))}
                <TableCell sx={subHeadCell}>Σύνολο</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COUNT_COLUMNS.length + 2} sx={{ ...bodyCell, py: "2rem" }}>
                    Δεν υπάρχουν ειδικευόμενοι.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell sx={{ ...bodyCell, textAlign: "left", whiteSpace: "nowrap" }}>{row.name}</TableCell>
                  <TableCell sx={{ ...bodyCell, textAlign: "left" }}>{row.specialty}</TableCell>
                  {COUNT_COLUMNS.map((key) => (
                    <DatesTooltip key={key} title={`${COLUMN_LABELS[key]} · ${row.name}`} entries={cellEntries([row], key)}>
                      <TableCell sx={key === "surgeryTotal" ? { ...bodyCell, ...surgeryTotalCell } : bodyCell}>
                        {row[key] || 0}
                      </TableCell>
                    </DatesTooltip>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ ...totalCell, textAlign: "left" }}>ΣΥΝΟΛΟ</TableCell>
                <TableCell sx={totalCell} />
                {COUNT_COLUMNS.map((key) => (
                  <DatesTooltip key={key} title={`${COLUMN_LABELS[key]} · ΣΥΝΟΛΟ`} entries={cellEntries(rows, key)}>
                    <TableCell sx={totalCell}>{totals[key]}</TableCell>
                  </DatesTooltip>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default ResidentsTable;
